import React, { createContext, useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { useUser, useAuth } from '../auth/clerk';
import { api } from '../api/client';
import { clearAllSceneDrafts, sweepOldSceneDrafts } from '../utils/sceneDraftLocal';
import { sameClerkUser, settleUserLoad, USER_RETRY_DELAYS_MS } from '../utils/userLoad';

/**
 * User context for accessing current user data throughout the app.
 *
 * Wraps Clerk's useUser and useAuth hooks to provide a consistent interface.
 * Fetches additional user data (like is_admin) from the backend.
 */
const UserContext = createContext(null);

export function UserProvider({ children }) {
  const { user, isLoaded: isUserLoaded, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const [backendUser, setBackendUser] = useState(null);
  const [isBackendLoaded, setIsBackendLoaded] = useState(false);
  const backendUserRef = useRef(null);

  // Unsent scene-draft copies live in this browser only: drop old ones on
  // start, and all of them when the account signs out or changes.
  useEffect(() => {
    sweepOldSceneDrafts();
  }, []);

  const lastClerkId = useRef(null);
  const clerkId = user?.id || null;
  useEffect(() => {
    if (!isUserLoaded) return;
    const previous = lastClerkId.current;
    if (previous && previous !== clerkId) clearAllSceneDrafts();
    lastClerkId.current = clerkId;
  }, [isUserLoaded, isSignedIn, clerkId]);

  // Fetch backend user data when authenticated.
  // A transient 500 keeps the last good user and retries without
  // putting the whole app back on a loading screen.
  useEffect(() => {
    if (!isUserLoaded) return undefined;
    if (!isSignedIn) {
      backendUserRef.current = null;
      setBackendUser(null);
      setIsBackendLoaded(true);
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    let tryIndex = 0;

    const apply = (settled) => {
      backendUserRef.current = settled.user;
      setBackendUser(settled.user);
      setIsBackendLoaded(settled.loaded);
    };

    const load = () => {
      api.get('/users/me')
        .then((data) => {
          if (cancelled) return;
          apply(settleUserLoad({ previousUser: backendUserRef.current, incoming: data, failed: false }));
        })
        .catch((error) => {
          if (cancelled) return;
          console.error('[UserContext] Failed to fetch backend user data:', error);
          const settled = settleUserLoad({
            previousUser: backendUserRef.current,
            incoming: null,
            failed: true,
          });
          apply(settled);
          if (!settled.retry || tryIndex >= USER_RETRY_DELAYS_MS.length) return;
          const delay = USER_RETRY_DELAYS_MS[tryIndex];
          tryIndex += 1;
          timer = setTimeout(load, delay);
        });
    };

    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isUserLoaded, isSignedIn]);

  // Clerk hands back a new user object on refresh. Keep the last one
  // when the account fields did not change, including a one-frame gap
  // where the object is missing.
  const clerkUserRef = useRef(user || null);
  if (user && !sameClerkUser(clerkUserRef.current, user)) {
    clerkUserRef.current = user;
  }
  const stableUser = clerkUserRef.current;

  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const stableGetToken = useCallback((options) => getTokenRef.current?.(options), []);

  const userId = stableUser?.id || null;
  const email = stableUser?.primaryEmailAddress?.emailAddress || null;
  const displayName = stableUser?.fullName || stableUser?.firstName || email;
  const imageUrl = stableUser?.imageUrl || null;
  const role = backendUser?.role || stableUser?.publicMetadata?.role || 'user';
  const isAdmin = backendUser?.is_admin ?? false;
  const plotGridAbsent = backendUser?.plot_grid_absent === true;
  const loaded = Boolean(isUserLoaded && isBackendLoaded);
  const signedIn = isSignedIn ?? false;

  const value = useMemo(() => ({
    user: stableUser,
    userId,
    email,
    displayName,
    imageUrl,
    isLoaded: loaded,
    isSignedIn: signedIn,
    role,
    isAdmin,
    plotGridAbsent,
    getToken: stableGetToken,
  }), [
    stableUser,
    userId,
    email,
    displayName,
    imageUrl,
    loaded,
    signedIn,
    role,
    isAdmin,
    plotGridAbsent,
    stableGetToken,
  ]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUserContext() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
}

export default UserContext;
